import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useSelector } from "react-redux";
import type { Appstore } from "../../../../redux";
import type { IPersonView } from "../../../../interfaces/models/IPersonView";
import {
  createInitialPersonFormState,
  personFormReducer,
} from "./personFormReducer";
import {
  ALL_SOCIAL_URL_FIELD_KEYS,
  type PersonSocialDisplayName,
} from "./socialNetworks";
import type {
  PersonFormErrorKey,
  PersonFormFieldKey,
  PersonFormState,
} from "./personFormTypes";
import { getEmptyPersonCreate } from "./getEmptyPersonCreate";
import {
  mapPersonCreateToSavePayload,
  mapPersonViewToPersonCreate,
} from "./mapPerson";
import {
  hasValidationErrors,
  validatePersonCreateForm,
} from "./personFormValidation";
import { isFormDirty } from "./personFormUtils";
import { addPerson, updatePerson } from "../../../../Services/Person";
import { toastError, toastPromise } from "../../../../utils/toast";
import {
  getCreatedPersonId,
  isPersonRoleDataAdmin,
} from "./personDataAdminUtils";
import { syncDataAdminsForNewPersonAssignments } from "./syncPersonDataAdmin";
import {
  hasFirmContactAssignmentChanges,
  validateFirmContactAssignments,
  type FirmContactAssignmentsMap,
} from "./firmContactAssignmentLogic";
import { syncFirmContactsForPersonAssignments } from "./syncPersonFirmContacts";
import type { FirmContactView } from "../../../../interfaces/models/IFirmContact";
import type { IOfficeNew } from "../../../../interfaces/models/IOffice";
import { mergeFirmContactsWithOffices } from "../../../../pages/ManageFirm/Professionals/manageDataAdminOfficeRules";
import { getFirms } from "../../../../Services/Firm";
import type IFirm from "../../../../interfaces/models/IFirm";
import { useAuth } from "../../../../store/AuthContext";
import {
  notifyProfilePhotoCacheBust,
  notifyProfilePhotoUpdated,
} from "../../../../utils/notifyProfilePhotoUpdated";
import { createProfilePhotoTimestamp } from "../../../../utils/profilePictureUrl";

export type FirmSourceMode = "redux" | "selectable";

export type FirmContactSyncApi = {
  assignments: FirmContactAssignmentsMap;
  offices: IOfficeNew[];
  firmContactData: FirmContactView[];
  membershipType?: string;
  firmName: string;
  editPersonId?: string;
};

export type PersonFormContextValue = {
  state: PersonFormState;
  mode: "add" | "edit";
  firmSource: FirmSourceMode;
  firmOptions: { id: string; name: string }[];
  firmOptionsLoading: boolean;
  firmLocked: boolean;
  form: PersonFormState["form"];
  setField: <K extends PersonFormFieldKey>(
    field: K,
    value: PersonFormState["form"][K],
  ) => void;
  setFormPatch: (patch: Partial<PersonFormState["form"]>) => void;
  updateSocialNetworkUrl: (name: PersonSocialDisplayName, url: string) => void;
  touchField: (field: PersonFormErrorKey) => void;
  touchFields: (fields: PersonFormFieldKey[]) => void;
  getFieldError: (field: PersonFormErrorKey) => string | undefined;
  shouldShowError: (field: PersonFormErrorKey) => boolean;
  isFieldDirty: (field: PersonFormFieldKey) => boolean;
  isDirty: boolean;
  submit: () => Promise<boolean>;
  submitting: boolean;
  initializeFromPerson: (person: IPersonView) => void;
  editPersonId?: string;
  editPersonUpdatedAt?: number;
  registerFirmContactSync: (api: FirmContactSyncApi | null) => void;
  firmContactValidationVisible: boolean;
};

const PersonFormContext = createContext<PersonFormContextValue | null>(null);

function buildInitialPersonFormState(
  mode: "add" | "edit",
  initialPerson: IPersonView | undefined,
  firmSource: FirmSourceMode,
  reduxFirmId: string,
): PersonFormState {
  if (mode === "edit" && initialPerson?.id) {
    return createInitialPersonFormState(
      mapPersonViewToPersonCreate(initialPerson),
    );
  }
  return createInitialPersonFormState(
    getEmptyPersonCreate(
      firmSource === "redux" && reduxFirmId ? { firm: reduxFirmId } : {},
    ),
  );
}

export type PersonFormProviderProps = {
  children: ReactNode;
  mode: "add" | "edit";
  firmSource?: FirmSourceMode;
  initialPerson?: IPersonView;
  onSubmitSuccess?: () => void;
  requireMainOffice?: boolean;
  requireProfessionalProfileFields?: boolean;
};

export function PersonFormProvider({
  children,
  mode,
  firmSource = "redux",
  initialPerson,
  onSubmitSuccess,
  requireMainOffice = false,
  requireProfessionalProfileFields = false,
}: PersonFormProviderProps) {
  const { loggedPerson, tokenDecoded } = useAuth();
  const manageFirm = useSelector((s: Appstore) => s.manageFirm);

  const reduxFirmId = manageFirm.selectedFirm?.id ?? "";

  const editBaselineRef = useRef<IPersonView | null>(
    mode === "edit" && initialPerson?.id ? initialPerson : null,
  );

  const lastEditPersonId = useRef<string | null>(
    mode === "edit" && initialPerson?.id ? initialPerson.id : null,
  );

  const [state, dispatch] = useReducer(
    personFormReducer,
    { mode, initialPerson, firmSource, reduxFirmId },
    (init) =>
      buildInitialPersonFormState(
        init.mode,
        init.initialPerson,
        init.firmSource,
        init.reduxFirmId,
      ),
  );

  const [firmOptions, setFirmOptions] = useState<IFirm[]>([]);
  const [firmOptionsLoading, setFirmOptionsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editPersonId, setEditPersonId] = useState<string | undefined>(
    mode === "edit" ? initialPerson?.id : undefined,
  );
  const [editPersonUpdatedAt, setEditPersonUpdatedAt] = useState<
    number | undefined
  >(mode === "edit" ? initialPerson?._ts : undefined);
  const [firmContactValidationVisible, setFirmContactValidationVisible] =
    useState(false);
  const firmContactSyncRef = useRef<FirmContactSyncApi | null>(null);

  const registerFirmContactSync = useCallback(
    (api: FirmContactSyncApi | null) => {
      firmContactSyncRef.current = api;
    },
    [],
  );

  const firmLocked = firmSource === "redux" && Boolean(reduxFirmId);

  useEffect(() => {
    if (firmSource !== "selectable") {
      return;
    }
    let cancelled = false;
    setFirmOptionsLoading(true);
    getFirms()
      .then((firms) => {
        if (!cancelled) {
          setFirmOptions(firms ?? []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFirmOptions([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setFirmOptionsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [firmSource]);

  useEffect(() => {
    if (mode !== "edit" || !initialPerson?.id) {
      return;
    }
    if (lastEditPersonId.current === initialPerson.id) {
      return;
    }
    lastEditPersonId.current = initialPerson.id;
    editBaselineRef.current = initialPerson;
    setEditPersonId(initialPerson.id);
    setEditPersonUpdatedAt(initialPerson._ts);
    dispatch({
      type: "INITIALIZE_FORM",
      payload: { form: mapPersonViewToPersonCreate(initialPerson) },
    });
  }, [mode, initialPerson?.id, initialPerson]);

  useEffect(() => {
    if (mode !== "add") {
      return;
    }
    lastEditPersonId.current = null;
    editBaselineRef.current = null;
    setEditPersonId(undefined);
    setEditPersonUpdatedAt(undefined);
    if (firmSource === "redux" && reduxFirmId) {
      dispatch({
        type: "INITIALIZE_FORM",
        payload: {
          form: getEmptyPersonCreate({ firm: reduxFirmId }),
        },
      });
    } else if (firmSource === "selectable") {
      dispatch({
        type: "INITIALIZE_FORM",
        payload: { form: getEmptyPersonCreate({ firm: "" }) },
      });
    }
  }, [mode, firmSource, reduxFirmId]);

  const setField = useCallback(
    <K extends PersonFormFieldKey>(
      field: K,
      value: PersonFormState["form"][K],
    ) => {
      dispatch({ type: "SET_FIELD", payload: { field, value } });
      dispatch({ type: "CLEAR_FIELD_ERROR", payload: field });
      if (field === "isAttorney" && value === false) {
        for (const k of ["areasOfPractice", "aopExpertiseIds"] as const) {
          dispatch({ type: "CLEAR_FIELD_ERROR", payload: k });
        }
      }
      if (field === "socialNetworks") {
        for (const k of ALL_SOCIAL_URL_FIELD_KEYS) {
          dispatch({ type: "CLEAR_FIELD_ERROR", payload: k });
        }
      }
    },
    [],
  );

  const setFormPatch = useCallback(
    (patch: Partial<PersonFormState["form"]>) => {
      dispatch({ type: "SET_FORM_PATCH", payload: patch });
      for (const key of Object.keys(patch) as PersonFormFieldKey[]) {
        // AOP: clearing aopExpertiseIds on every areasOfPractice patch hid the "My Areas"
        // error until expertise was chosen — only clear when the patched list is non-empty.
        if (key === "aopExpertiseIds") {
          const next = patch.aopExpertiseIds;
          if (Array.isArray(next) && next.length > 0) {
            dispatch({ type: "CLEAR_FIELD_ERROR", payload: key });
          }
          continue;
        }
        if (key === "areasOfPractice") {
          const next = patch.areasOfPractice;
          if (Array.isArray(next) && next.length > 0) {
            dispatch({ type: "CLEAR_FIELD_ERROR", payload: key });
          }
          continue;
        }
        dispatch({ type: "CLEAR_FIELD_ERROR", payload: key });
      }
      if (patch.socialNetworks !== undefined) {
        for (const k of ALL_SOCIAL_URL_FIELD_KEYS) {
          dispatch({ type: "CLEAR_FIELD_ERROR", payload: k });
        }
      }
    },
    [],
  );

  const updateSocialNetworkUrl = useCallback(
    (name: PersonSocialDisplayName, url: string) => {
      const t = url.trim();
      const first = state.form.name?.trim() ?? "";
      const last = state.form.lastName?.trim() ?? "";
      const full = `${first} ${last}`.trim();
      if (t && full && t === full) {
        return;
      }
      if (t && first && t === first) {
        return;
      }
      if (t && last && t === last) {
        return;
      }
      dispatch({ type: "SET_SOCIAL_NETWORK_URL", payload: { name, url } });
    },
    [state.form.name, state.form.lastName],
  );

  const touchField = useCallback((field: PersonFormErrorKey) => {
    dispatch({ type: "SET_TOUCHED", payload: { field, touched: true } });
  }, []);

  const touchFields = useCallback((fields: PersonFormFieldKey[]) => {
    const next: Record<string, boolean> = {};
    for (const f of fields) {
      next[f] = true;
    }
    dispatch({ type: "SET_TOUCHED_MANY", payload: next });
  }, []);

  const shouldShowError = useCallback(
    (field: PersonFormErrorKey) =>
      Boolean(state.touched[field]) || state.submitCount > 0,
    [state.touched, state.submitCount],
  );

  const getFieldError = useCallback(
    (field: PersonFormErrorKey) => {
      if (!shouldShowError(field)) {
        return undefined;
      }
      return state.errors[field];
    },
    [shouldShowError, state.errors],
  );

  const isFieldDirty = useCallback(
    (field: PersonFormFieldKey) => Boolean(state.dirtyFields[field]),
    [state.dirtyFields],
  );

  const isDirty = useMemo(
    () => isFormDirty(state.dirtyFields),
    [state.dirtyFields],
  );

  const acceptedEmailDomainsForValidation = useMemo(() => {
    const firmId = state.form.firm;
    const sel = manageFirm.selectedFirm as IFirm | undefined;
    let out: string[] | undefined;

    if (!firmId) {
      out = undefined;
    } else if (firmSource === "redux") {
      if (sel?.id === firmId && Array.isArray(sel.acceptedEmailDomain)) {
        out = sel.acceptedEmailDomain;
      } else {
        out = undefined;
      }
    } else {
      out = undefined;
    }

    if (out === undefined && firmId) {
      const fromList = firmOptions.find((f) => f.id === firmId);
      if (fromList && Array.isArray(fromList.acceptedEmailDomain)) {
        out = fromList.acceptedEmailDomain;
      }
    }

    return out;
  }, [state.form.firm, firmSource, manageFirm.selectedFirm, firmOptions]);

  const initializeFromPerson = useCallback((person: IPersonView) => {
    if (person.id) {
      lastEditPersonId.current = person.id;
      editBaselineRef.current = person;
      setEditPersonId(person.id);
      setEditPersonUpdatedAt(person._ts);
    }
    dispatch({
      type: "INITIALIZE_FORM",
      payload: { form: mapPersonViewToPersonCreate(person) },
    });
  }, []);

  const submit = useCallback(async (): Promise<boolean> => {
    setSubmitting(true);
    try {
      dispatch({ type: "INCREMENT_SUBMIT" });
      const { errors, hasMissingFields, hasValidationFormatErrors } =
      validatePersonCreateForm(state.form, {
        acceptedEmailDomains: acceptedEmailDomainsForValidation,
        requireMainOffice,
        requireProfessionalProfileFields,
        actorRole:
          tokenDecoded?.extension_WSG_Permissions ?? loggedPerson?.role,
        existingRole:
          mode === "edit" ? editBaselineRef.current?.role : undefined,
      });
      console.log(errors)
      dispatch({ type: "SET_ERRORS", payload: errors });
      if (hasValidationErrors(errors)) {
        if (hasMissingFields && hasValidationFormatErrors) {
          toastError(
            "There are missing fields and validation errors. Please fill them in and correct the format before saving.",
          );
        } else if (hasMissingFields) {
          toastError(
            "There are missing fields. Please fill them in before saving.",
          );
        } else {
          toastError(
            "There are validation errors. Please review the format (email, URLs, etc.).",
          );
        }
        return false;
      }

      const firmContactSync = firmContactSyncRef.current;
      const personFullName =
        `${state.form.name ?? ""} ${state.form.lastName ?? ""}`.trim();
      const existing =
        mode === "edit" && editBaselineRef.current?.id
          ? editBaselineRef.current
          : undefined;
      const editPersonId = existing?.id ?? firmContactSync?.editPersonId;

      if (
        firmContactSync &&
        hasFirmContactAssignmentChanges(firmContactSync.assignments)
      ) {
        const mergedContacts = mergeFirmContactsWithOffices(
          firmContactSync.firmContactData,
          firmContactSync.offices,
          String(state.form.firm ?? "").trim(),
          firmContactSync.firmName,
          firmContactSync.membershipType,
        );
        const firmContactValidationError = validateFirmContactAssignments(
          firmContactSync.assignments,
          mergedContacts,
          {
            personId: editPersonId,
            personFullName,
            isAttorney: state.form.isAttorney,
          },
        );
        if (firmContactValidationError) {
          setFirmContactValidationVisible(true);
          toastError(firmContactValidationError);
          return false;
        }
      }

      setFirmContactValidationVisible(false);
      const payload = mapPersonCreateToSavePayload(state.form, existing);
      const shouldSyncDataAdmin = isPersonRoleDataAdmin(state.form.role);
      const firmId = String(state.form.firm ?? "").trim();

      if (mode === "add") {
        const created = await toastPromise(addPerson(payload), {
          pending: "Saving person...",
          success: "Person created successfully.",
        });
        const createdPersonId = getCreatedPersonId(created);
        if (shouldSyncDataAdmin && firmId && createdPersonId) {
          await toastPromise(
            syncDataAdminsForNewPersonAssignments(firmId, createdPersonId),
            {
              pending: "Saving Data Admin assignment...",
              success: "Data Admin assignment saved.",
            },
          );
        }
        if (
          firmContactSync &&
          hasFirmContactAssignmentChanges(firmContactSync.assignments) &&
          createdPersonId
        ) {
          await toastPromise(
            syncFirmContactsForPersonAssignments({
              firmId,
              firmName: firmContactSync.firmName,
              membershipType: firmContactSync.membershipType,
              offices: firmContactSync.offices,
              firmContactData: firmContactSync.firmContactData,
              assignments: firmContactSync.assignments,
              personId: createdPersonId,
              personFullName,
              personEmail: String(state.form.firmEmail ?? "").trim(),
            }),
            {
              pending: "Saving firm contact assignments...",
              success: "Firm contact assignments saved.",
            },
          );
        }
      } else {
        await toastPromise(updatePerson(payload), {
          pending: "Updating person...",
          success: "Person updated successfully.",
        });
        if (shouldSyncDataAdmin && firmId && existing?.id) {
          const dataAdminResult = await syncDataAdminsForNewPersonAssignments(
            firmId,
            existing.id,
            state.form.mainOffice,
          );
          if (dataAdminResult) {
            toastPromise(Promise.resolve(dataAdminResult), {
              success: "Data Admin assignment saved.",
            });
          }
        }
        if (
          firmContactSync &&
          hasFirmContactAssignmentChanges(firmContactSync.assignments) &&
          existing?.id
        ) {
          await toastPromise(
            syncFirmContactsForPersonAssignments({
              firmId,
              firmName: firmContactSync.firmName,
              membershipType: firmContactSync.membershipType,
              offices: firmContactSync.offices,
              firmContactData: firmContactSync.firmContactData,
              assignments: firmContactSync.assignments,
              personId: existing.id,
              personFullName,
              personEmail: String(state.form.firmEmail ?? "").trim(),
            }),
            {
              pending: "Saving firm contact assignments...",
              success: "Firm contact assignments saved.",
            },
          );
        }
      }
      if (!state.form.unlistedTitleToggle) {
        setField("unlistedTitle", "");
      }
      if (mode === "edit" && existing?.id && state.dirtyFields.photo) {
        if (loggedPerson?.id === existing.id) {
          notifyProfilePhotoUpdated({
            ...loggedPerson,
            photo: payload.photoRemoved ? "" : createProfilePhotoTimestamp(),
          });
        } else {
          notifyProfilePhotoCacheBust(existing.id);
        }
      }
      onSubmitSuccess?.();
      return true;
    } finally {
      setSubmitting(false);
    }
  }, [
    mode,
    state.form,
    setField,
    acceptedEmailDomainsForValidation,
    requireMainOffice,
    requireProfessionalProfileFields,
    onSubmitSuccess,
    loggedPerson,
    tokenDecoded,
  ]);

  const firmOptionsMapped = useMemo(
    () =>
      firmOptions.map((f) => ({
        id: f.id,
        name: f.firmName || f.name,
      })),
    [firmOptions],
  );

  const value = useMemo<PersonFormContextValue>(
    () => ({
      state,
      mode,
      firmSource,
      firmOptions: firmOptionsMapped,
      firmOptionsLoading,
      firmLocked,
      form: state.form,
      setField,
      setFormPatch,
      updateSocialNetworkUrl,
      touchField,
      touchFields,
      getFieldError,
      shouldShowError,
      isFieldDirty,
      isDirty,
      submit,
      submitting,
      initializeFromPerson,
      editPersonId,
      editPersonUpdatedAt,
      registerFirmContactSync,
      firmContactValidationVisible,
    }),
    [
      state,
      mode,
      firmSource,
      firmOptionsMapped,
      firmOptionsLoading,
      firmLocked,
      editPersonId,
      editPersonUpdatedAt,
      setField,
      setFormPatch,
      updateSocialNetworkUrl,
      touchField,
      touchFields,
      getFieldError,
      shouldShowError,
      isFieldDirty,
      isDirty,
      submit,
      submitting,
      initializeFromPerson,
      registerFirmContactSync,
      firmContactValidationVisible,
    ],
  );

  return (
    <PersonFormContext.Provider value={value}>
      {children}
    </PersonFormContext.Provider>
  );
}

export function usePersonForm(): PersonFormContextValue {
  const ctx = useContext(PersonFormContext);
  if (!ctx) {
    throw new Error("usePersonForm must be used within PersonFormProvider");
  }
  return ctx;
}

export function useRegisterFirmContactSync(
  api: FirmContactSyncApi | null,
): void {
  const { registerFirmContactSync } = usePersonForm();
  useEffect(() => {
    registerFirmContactSync(api);
    return () => registerFirmContactSync(null);
  }, [api, registerFirmContactSync]);
}
